import React, { useState, useEffect } from 'react';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';

function EmployeeLeaveTab({ employeeId, leave, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: 'Annual Leave',
    start_date: '',
    end_date: '',
    days: 1,
    status: 'Pending',
    authorized: true,
    notes: ''
  });

  useEffect(() => {
    fetchLeaveBalance();
  }, [employeeId, leave]);
  
  const fetchLeaveBalance = async () => {
    try {
      const response = await apiFetch(`/api/leave-balance/${employeeId}`);
      const data = await response.json();
      setLeaveBalance(data);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };
  
  // Sorting
  const { items: sortedLeave, requestSort, getSortIndicator } = useSortableTable(leave, { key: 'start_date', direction: 'desc' });
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
    return diffDays;
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      leave_type: 'Annual Leave',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      days: 1,
      status: 'Pending',
      authorized: true, // Annual Leave defaults to authorized
      notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      leave_type: item.leave_type || 'Annual Leave',
      start_date: item.start_date ? item.start_date.split('T')[0] : '',
      end_date: item.end_date ? item.end_date.split('T')[0] : '',
      days: item.days_count || item.days || 1,
      status: item.status || 'Pending',
      authorized: item.authorized !== undefined ? item.authorized : true,
      notes: item.notes || ''
    });
    setShowModal(true);
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    // Auto-calculate days when both dates are set
    if (newData.start_date && newData.end_date) {
      newData.days = calculateDays(newData.start_date, newData.end_date);
    }
    setFormData(newData);
  };

  const handleSave = async () => {
    if (!formData.start_date || !formData.end_date) {
      alert('Start and end dates are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingItem
        ? `/api/employees/leave/${editingItem.id}`
        : '/api/employees/leave';

      const response = await apiFetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          employee_id: employeeId
        })
      });

      if (response.ok) {
        setShowModal(false);
        onRefresh();
        fetchLeaveBalance(); // Refresh balance after save
      } else {
        alert('Error saving leave request');
      }
    } catch (error) {
      console.error('Error saving leave:', error);
      alert('Error saving leave request');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, leaveType) => {
    if (!window.confirm(`Delete ${leaveType} request?`)) return;

    try {
      await apiFetch(`/api/employees/leave/${id}`, {
        method: 'DELETE'
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting leave:', error);
      alert('Error deleting leave request');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Annual Leave Balance Summary */}
        {leaveBalance && leaveBalance.entitlement && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-xs text-gray-600">Annual Leave Entitlement</p>
                  <p className="text-lg font-bold text-gray-900">{leaveBalance.entitlement} days</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Taken</p>
                  <p className="text-lg font-bold text-blue-600">{leaveBalance.taken} days</p>
                </div>
                {leaveBalance.pending > 0 && (
                  <div>
                    <p className="text-xs text-gray-600">Pending</p>
                    <p className="text-lg font-bold text-yellow-600">{leaveBalance.pending} days</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-600">Remaining</p>
                  <p className={`text-lg font-bold ${
                    leaveBalance.remaining < 0 ? 'text-red-600' :
                    leaveBalance.remaining < 5 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {leaveBalance.remaining} days
                  </p>
                </div>
              </div>
              {leaveBalance.remaining < 5 && (
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                  leaveBalance.remaining < 0 ? 'bg-red-600 text-white' : 'bg-yellow-600 text-white'
                }`}>
                  {leaveBalance.remaining < 0 ? 'OVERDRAWN' : 'LOW'}
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Leave & Absence</h2>
          <button 
            onClick={handleAdd}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded transition-colors"
          >
            + Add Leave Request
          </button>
        </div>

      {leave.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No leave records</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('leave_type')}
                >
                  Leave Type{getSortIndicator('leave_type')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('start_date')}
                >
                  Start Date{getSortIndicator('start_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('end_date')}
                >
                  End Date{getSortIndicator('end_date')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('days_count')}
                >
                  Days{getSortIndicator('days_count')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase cursor-pointer hover:bg-gray-400 transition-colors select-none"
                  onClick={() => requestSort('status')}
                >
                  Status{getSortIndicator('status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedLeave.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 1 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{item.leave_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.start_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(item.end_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{item.days_count || item.days}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      item.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.leave_type)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Add/Edit Modal */}
    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-slate-600 text-white px-6 py-4 rounded-t-lg">
            <h3 className="text-xl font-semibold">
              {editingItem ? 'Edit Leave Request' : 'Add Leave Request'}
            </h3>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
                <select
                  value={formData.leave_type}
                  onChange={(e) => {
                    const leaveType = e.target.value;
                    // Auto-set authorized based on leave type
                    const isAuthorized = ['Annual Leave', 'Maternity/Paternity', 'Compassionate Leave'].includes(leaveType);
                    setFormData({...formData, leave_type: leaveType, authorized: isAuthorized});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="Annual Leave">Annual Leave</option>
                  <option value="Sick Leave">Sick Leave</option>
                  <option value="Unpaid Leave">Unpaid Leave</option>
                  <option value="Compassionate Leave">Compassionate Leave</option>
                  <option value="Maternity/Paternity">Maternity/Paternity</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleDateChange('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Days</label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.days}
                  onChange={(e) => setFormData({...formData, days: parseFloat(e.target.value) || 1})}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from dates (can override)</p>
              </div>
            </div>

            {/* Annual Leave Balance Warning */}
            {formData.leave_type === 'Annual Leave' && leaveBalance && (
              <div className={`p-4 rounded-lg border-2 ${
                (leaveBalance.remaining - formData.days) < 0 ? 'bg-red-50 border-red-500' :
                (leaveBalance.remaining - formData.days) < 5 ? 'bg-yellow-50 border-yellow-500' :
                'bg-blue-50 border-blue-500'
              }`}>
                <p className="text-sm font-medium text-gray-900">
                  Annual Leave Balance Check:
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span>Remaining: <strong>{leaveBalance.remaining} days</strong></span>
                  <span>→</span>
                  <span>Requesting: <strong>{formData.days} days</strong></span>
                  <span>→</span>
                  <span className={
                    (leaveBalance.remaining - formData.days) < 0 ? 'text-red-700 font-bold' :
                    (leaveBalance.remaining - formData.days) < 5 ? 'text-yellow-700 font-bold' :
                    'text-green-700 font-bold'
                  }>
                    After: {leaveBalance.remaining - formData.days} days
                  </span>
                </div>
                {(leaveBalance.remaining - formData.days) < 0 && (
                  <p className="text-sm text-red-700 font-medium mt-2">
                    ⚠️ This request exceeds available entitlement!
                  </p>
                )}
              </div>
            )}
            
            {/* Bradford Factor - Authorized Absence Checkbox */}
            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="authorized"
                  checked={formData.authorized}
                  onChange={(e) => setFormData({...formData, authorized: e.target.checked})}
                  className="mt-1 h-4 w-4 text-slate-600 focus:ring-slate-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <label htmlFor="authorized" className="text-sm font-medium text-gray-900 cursor-pointer">
                    Authorized Absence
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    {formData.authorized 
                      ? '✅ Does NOT count toward Bradford Factor score' 
                      : '⚠️ COUNTS toward Bradford Factor (affects attendance score)'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Annual leave, maternity/paternity always authorized. Sick leave = unauthorized (manager can override).
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="Reason for leave, doctor's note reference, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded disabled:opacity-50"
            >
              {saving ? 'Saving...' : (editingItem ? 'Update' : 'Submit Request')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}

export default EmployeeLeaveTab;
